/* eslint-disable no-underscore-dangle */
import axios, {
  AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse,
} from 'axios';
import store from '@/features/common/store';
import router from '@/router';
import { redirectTo } from '@/features/common/helpers';
import { getToken, getIsTokenExpiredOrNearExpiry, refreshToken } from '@/features/auth/helpers';
import { exceptionsAndPagesToRedirect } from '../constants';

interface AxiosCustomErrorConfig extends AxiosRequestConfig {
  _retry?: boolean
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
let refreshFailSubscribers: ((error: unknown) => void)[] = [];

const onRefreshed = (token: string): void => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  refreshFailSubscribers = [];
};

const onRefreshFailed = (error: unknown): void => {
  refreshFailSubscribers.forEach((cb) => cb(error));
  refreshSubscribers = [];
  refreshFailSubscribers = [];
};

const addRefreshSubscriber = (
  onSuccess: (token: string) => void,
  onFailure: (error: unknown) => void,
): void => {
  refreshSubscribers.push(onSuccess);
  refreshFailSubscribers.push(onFailure);
};

const handledExceptions = new Map<string, boolean>();
let hasAccountExceptionBeenHandled = false;

const forceLogout = (): void => {
  store.dispatch('logout');
  const basePath = process.env.VUE_APP_PUBLIC_PATH || '';
  window.location.assign(`${basePath}/sign-in`);
};

const handleAccountException = (routeName: string): void => {
  if (handledExceptions.get(routeName)) {
    return;
  }

  handledExceptions.set(routeName, true);
  hasAccountExceptionBeenHandled = true;
  router.push({ name: routeName });
};

const extractErrorTypeFromResponse = (responseData: unknown): string | null => {
  try {
    if (typeof responseData === 'string') {
      const jsonMatch = responseData.match(/\{\s*"type"\s*:\s*"[^"]+"\s*,\s*"message"\s*:\s*"[^"]+"\s*\}/);
      if (jsonMatch) {
        const parsedData = JSON.parse(jsonMatch[0]);
        return parsedData?.type || null;
      }
      const exceptionTypes = ['CurrentUserNotFoundException', 'IsLoginDisabledException'];
      for (const exceptionType of exceptionTypes) {
        if (responseData.includes(exceptionType)) {
          return exceptionType;
        }
      }
      return null;
    }

    if (responseData && typeof responseData === 'object') {
      const data = responseData as {
        // eslint-disable-next-line @typescript-eslint/ban-types
        nestedException?: { detail?: string | object, type?: string }
      };
      const { nestedException } = data;

      if (nestedException?.type) {
        return nestedException.type;
      }

      if (nestedException?.detail) {
        try {
          const parsedDetail = typeof nestedException.detail === 'string'
            ? JSON.parse(nestedException.detail)
            : nestedException.detail;
          return (parsedDetail as { type?: string })?.type || null;
        } catch {
          return null;
        }
      }
    }
  } catch {
    return null;
  }

  return null;
};

const formatBearerToken = (token: string): string => {
  const formattedToken = `Bearer ${encodeURIComponent(token.replace(/"/g, '').trim())}`;
  return formattedToken;
};

/**
 * Initiate axios setup with specified baseURL and version
 *
 * @param version ex: 1 (note: /v1 will automatically add to the API URL)
 * @param baseURL ex: https://test.secureship.ca/ship/api (note: do not add trailing slash(/))
 *
 * @returns AxiosConfiguration { axiosService: AxiosInstance, baseURL: string, version: number }
 */
export function useAxios(version = 1, baseURL = '', withoutBaseUrl = false) :{
  axiosService: AxiosInstance,
  baseURL: string,
  version: number,
} {
  const initialBaseUrl = baseURL || process.env.VUE_APP_API_BASE_URL;
  const axiosBaseUrl = withoutBaseUrl ? '' : `${initialBaseUrl}/v${version}`;

  const axiosService = axios.create({
    baseURL: axiosBaseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  axiosService.interceptors.request.use(
    async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
      const configCopy = { ...config };
      const token = getToken();

      const isRefreshTokenRequest = configCopy.url?.includes('/auth/refresh-token');

      if (token && !isRefreshTokenRequest && getIsTokenExpiredOrNearExpiry()) {
        if (isRefreshing) {
          return new Promise<AxiosRequestConfig>((resolve, reject) => {
            addRefreshSubscriber(
              (newToken: string) => {
                configCopy.headers.Authorization = formatBearerToken(newToken);
                resolve(configCopy);
              },
              (refreshError: unknown) => {
                reject(refreshError);
              },
            );
          });
        }

        isRefreshing = true;

        try {
          const { axiosService: localAxiosService } = useAxios(1);
          const isSuccess = await refreshToken(localAxiosService);
          if (!isSuccess) throw new Error('Token is not found');

          const newToken = getToken();
          isRefreshing = false;
          onRefreshed(newToken);

          configCopy.headers.Authorization = formatBearerToken(newToken);
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshFailed(refreshError);
          forceLogout();
          throw refreshError;
        }
      } else if (token) {
        configCopy.headers.Authorization = formatBearerToken(token);
      }

      return configCopy;
    }, (error: AxiosError) => {
      return Promise.reject(error);
    },
  );

  axiosService.interceptors.response.use((response) => response, async (error: AxiosError) => {
    const originalConfig = error.config as AxiosCustomErrorConfig;
    const response = error.response as AxiosResponse;

    const isOnAccountExceptionPage = window.location.pathname.includes('/account-disabled')
      || window.location.pathname.includes('/account-deleted');
    const shouldPreventRedirect = isOnAccountExceptionPage || hasAccountExceptionBeenHandled;

    if ((response.status === 401 && originalConfig.url?.includes('/auth/refresh-token'))
    || (response.status === 403 && originalConfig.url?.includes('/auth/refresh-token'))) {
      if (!shouldPreventRedirect) {
        forceLogout();
      }
      return Promise.reject(error);
    }

    if (response.status === 401 && !originalConfig._retry) {
      if (shouldPreventRedirect) {
        return Promise.reject(error);
      }

      originalConfig._retry = true;

      // If a refresh is already in flight, queue this request to retry after it completes
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber(
            (token: string) => {
              originalConfig.headers.Authorization = formatBearerToken(token);
              resolve(axiosService(originalConfig));
            },
            (refreshError: unknown) => {
              reject(refreshError);
            },
          );
        });
      }

      isRefreshing = true;

      try {
        const { axiosService: localAxiosService } = useAxios(1);
        const isSuccess = await refreshToken(localAxiosService);
        if (!isSuccess) throw new Error('Token is not found');

        const newToken = getToken();
        axiosService.defaults.headers.common.Authorization = formatBearerToken(newToken);
        isRefreshing = false;
        onRefreshed(newToken);

        return axiosService(originalConfig);
      } catch (tokenError) {
        isRefreshing = false;
        onRefreshFailed(tokenError);
        if (!shouldPreventRedirect) {
          forceLogout();
        }
        return Promise.reject(tokenError);
      }
    }

    // blob response type handling
    if (
      error.request
        && error.request.responseType === 'blob'
        && response.data instanceof Blob
        && response.data.type
        && response.data.type.toLowerCase().indexOf('json') !== -1
    ) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          response.data = JSON.parse(reader.result as string);
          resolve(Promise.reject(error));
        };

        reader.onerror = () => {
          reject(error);
        };

        reader.readAsText(response.data);
      });
    }

    const exceptionRouteMap: Record<string, string> = {
      IsLoginDisabledException: 'account-disabled',
      CurrentUserNotFoundException: 'account-deleted',
    };

    if (response.status === 500) {
      const errorType = extractErrorTypeFromResponse(error.response?.data);

      if (errorType && exceptionRouteMap[errorType]) {
        handleAccountException(exceptionRouteMap[errorType]);
        return Promise.reject(error);
      }
    }

    if (response.status === 422) {
      const { detail } = error.response?.data;

      if (detail) {
        const errorType = JSON.parse(detail).type;

        if (errorType && exceptionRouteMap[errorType]) {
          handleAccountException(exceptionRouteMap[errorType]);
          return Promise.reject(error);
        }

        const matchedData = exceptionsAndPagesToRedirect.find(
          (data) => data.exception === errorType,
        );

        const shouldRedirect = matchedData
          && !window.location.pathname.includes(matchedData.page.split('?')[0])
          && !matchedData.stopPropagation?.();

        if (shouldRedirect && matchedData?.external) {
          redirectTo(matchedData.page);
        } else if (shouldRedirect && matchedData?.page) {
          router.push(matchedData.page);
        }
      }
    }

    return Promise.reject(error);
  });

  return {
    axiosService,
    baseURL: axiosBaseUrl,
    version,
  };
}
